import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  Calculator,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  Percent,
  ShieldCheck,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  useDashboard,
  type Profile,
} from "@/features/dashboard/dashboard-context";
import { EnvironmentBadge, PageHead, Panel } from "@/components/bits";
import { accountApi, publicApi } from "@/services/api-client";
import { ErrorBox, errorMessage } from "@/features/onboarding/shared";
import { useI18n, LanguageFlag } from "@/i18n/i18n-context";
import { cn } from "@/lib/utils";
import { fmtAmount, record, text, useServer } from "@/features/finance/kit";

const CHANNEL_CODES = ["EMAIL", "SMS", "PUSH", "INAPP", "WHATSAPP"] as const;
type Channel = (typeof CHANNEL_CODES)[number];
const EVENT_CODES = [
  "payment.completed",
  "kyc.approved",
  "kyb.approved",
] as const;
type ChannelPref = { enabled: boolean; events: string[] };
type SettingsTab = "profile" | "security" | "notifications" | "fees";

export default function Settings() {
  const d = useDashboard();
  const { toast } = useToast();
  const { t, lang, setLanguage } = useI18n();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [p, setP] = useState<Profile>(d.profile);
  const [err, setErr] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [prefs, setPrefs] = useState<Record<Channel, ChannelPref>>({
    EMAIL: { enabled: true, events: [...EVENT_CODES] },
    SMS: { enabled: false, events: [] },
    PUSH: { enabled: true, events: [...EVENT_CODES] },
    INAPP: { enabled: true, events: [...EVENT_CODES] },
    WHATSAPP: { enabled: false, events: [] },
  });
  const [savingCh, setSavingCh] = useState<Channel | null>(null);
  const [prefError, setPrefError] = useState("");
  const [emailStep, setEmailStep] = useState<
    "IDLE" | "OTP_SENT" | "OTP_VERIFIED"
  >("IDLE");
  const [otpCode, setOtpCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Platform Channels Availability
  const [channelAvailability, setChannelAvailability] = useState<
    Record<string, { available: boolean; status?: string }>
  >({
    EMAIL: { available: true },
    IN_APP: { available: true },
    PUSH: { available: true },
    SMS: { available: false },
    WHATSAPP: { available: false },
  });

  useEffect(() => {
    accountApi<Record<string, { available: boolean; status?: string }>>(
      "/channels/status",
    )
      .then((res) => {
        if (res.data) setChannelAvailability(res.data);
      })
      .catch(() => {});
  }, []);

  const isChannelAvailable = (ch: Channel) => {
    if (ch === "SMS") return Boolean(channelAvailability.SMS?.available);
    if (ch === "WHATSAPP")
      return Boolean(channelAvailability.WHATSAPP?.available);
    return true;
  };

  // Two-Factor Authentication state
  const [twoFactorData, setTwoFactorData] = useState<{
    enabled: boolean;
    channel: string;
    confirmed_at: string | null;
  } | null>(null);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [selected2FaChannel, setSelected2FaChannel] = useState<
    "EMAIL" | "SMS" | "WHATSAPP"
  >("EMAIL");
  const [twoFactorStep, setTwoFactorStep] = useState<
    "IDLE" | "OTP_SENT" | "RECOVERY_DISPLAY"
  >("IDLE");
  const [twoFactorOtp, setTwoFactorOtp] = useState("");
  const [twoFactorDestination, setTwoFactorDestination] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const loadTwoFactor = useCallback(async () => {
    try {
      const res = await accountApi<{
        enabled: boolean;
        channel: string;
        confirmed_at: string | null;
      }>("/security/two-factor", { authenticated: true });
      if (res.data) {
        setTwoFactorData(res.data);
        if (
          res.data.channel &&
          ["EMAIL", "SMS", "WHATSAPP"].includes(res.data.channel)
        ) {
          setSelected2FaChannel(
            res.data.channel as "EMAIL" | "SMS" | "WHATSAPP",
          );
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void loadTwoFactor();
  }, [loadTwoFactor]);

  const sendTwoFactorOtp = async () => {
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      const res = await accountApi<{ destination: string; sent: boolean }>(
        "/security/two-factor/send-otp",
        {
          method: "POST",
          authenticated: true,
          body: { channel: selected2FaChannel, language: lang },
        },
      );
      setTwoFactorDestination(res.data.destination);
      setTwoFactorStep("OTP_SENT");
      toast({ title: "Code de sécurité envoyé !" });
    } catch (c) {
      setTwoFactorError(errorMessage(c));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const confirmTwoFactorOtp = async () => {
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      const res = await accountApi<{
        enabled: boolean;
        recovery_codes: string[];
      }>("/security/two-factor/confirm", {
        method: "POST",
        authenticated: true,
        retryOnUnauthorized: false,
        body: { channel: selected2FaChannel, code: twoFactorOtp.trim() },
      });
      setRecoveryCodes(res.data.recovery_codes || []);
      setTwoFactorStep("RECOVERY_DISPLAY");
      toast({ title: t("settings.twoFactorSuccessToast") });
      await loadTwoFactor();
    } catch (c) {
      setTwoFactorError(errorMessage(c));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!window.confirm(t("settings.twoFactorDisableConfirm"))) return;
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      await accountApi("/security/two-factor", {
        method: "DELETE",
        authenticated: true,
        retryOnUnauthorized: false,
        body: { code: "CONFIRM" },
      });
      toast({ title: t("settings.twoFactorDisabledToast") });
      setTwoFactorStep("IDLE");
      setTwoFactorOtp("");
      await loadTwoFactor();
    } catch (c) {
      setTwoFactorError(errorMessage(c));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  // Fee Checker & Simulator state
  const feesRes = useServer<unknown>("/dashboard/fees");
  const feesData = useMemo(() => record(feesRes.data), [feesRes.data]);
  const collectionPct = Number(
    text(feesData, "collection_fee_percentage", "collection.percentage") ||
      "4.5",
  );
  const distributionPct = Number(
    text(feesData, "distribution_fee_percentage", "distribution.percentage") ||
      "3",
  );
  const internalTransferPct = Number(
    text(
      feesData,
      "internal_transfer_fee_percentage",
      "internal_transfer.percentage",
    ) || "0",
  );

  const [simOp, setSimOp] = useState<"COLLECTION" | "DISTRIBUTION">(
    "COLLECTION",
  );
  const [simAmount, setSimAmount] = useState("10000");
  const [simCurrency, setSimCurrency] = useState("XAF");
  const [simPayer, setSimPayer] = useState<"MERCHANT" | "CUSTOMER">("MERCHANT");

  useEffect(() => setP(d.profile), [d.profile]);

  const channelsMeta: Array<[Channel, string, string]> = [
    ["EMAIL", t("settings.channelEmail"), t("settings.channelEmailSub")],
    ["SMS", t("settings.channelSms"), t("settings.channelSmsSub")],
    ["PUSH", t("settings.channelPush"), t("settings.channelPushSub")],
    ["INAPP", t("settings.channelInApp"), t("settings.channelInAppSub")],
    [
      "WHATSAPP",
      t("settings.channelWhatsApp"),
      t("settings.channelWhatsAppSub"),
    ],
  ];

  const eventsMeta: Array<[string, string]> = [
    ["payment.completed", t("settings.eventPaymentCompleted")],
    ["kyc.approved", t("settings.eventKycApproved")],
    ["kyb.approved", t("settings.eventKybApproved")],
  ];

  const simResult = useMemo(() => {
    const raw = Number(simAmount.replace(/[^\d.]/g, "")) || 0;
    const isZeroDecimal = ["XAF", "XOF", "GNF", "RWF", "UGX"].includes(
      simCurrency,
    );
    const round = (v: number) =>
      isZeroDecimal ? Math.round(v) : Math.round(v * 100) / 100;

    if (simOp === "COLLECTION") {
      const fee = round((raw * collectionPct) / 100);
      if (simPayer === "CUSTOMER") {
        return {
          charged: round(raw + fee),
          fee,
          net: round(raw),
          rate: collectionPct,
        };
      }
      return {
        charged: round(raw),
        fee,
        net: Math.max(0, round(raw - fee)),
        rate: collectionPct,
      };
    }

    const fee = round((raw * distributionPct) / 100);
    return {
      payout: round(raw),
      fee,
      totalDebit: round(raw + fee),
      rate: distributionPct,
    };
  }, [simAmount, simCurrency, simOp, simPayer, collectionPct, distributionPct]);

  const requestEmailOtp = async () => {
    setEmailBusy(true);
    setEmailError("");
    try {
      await publicApi("/profile/email/otp", {
        method: "POST",
        body: { language: lang },
      });
      setEmailStep("OTP_SENT");
      toast({
        title:
          lang === "en"
            ? "Verification code sent to your email"
            : "Code OTP envoyé par e-mail",
      });
    } catch (c) {
      setEmailError(errorMessage(c));
    } finally {
      setEmailBusy(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!/^\d{6}$/.test(otpCode.trim())) {
      setEmailError(
        lang === "en"
          ? "Enter the 6-digit OTP code received by email."
          : "Saisissez le code OTP à 6 chiffres reçu par e-mail.",
      );
      return;
    }
    setEmailBusy(true);
    setEmailError("");
    try {
      await publicApi("/profile/email/verify", {
        method: "POST",
        body: { otp: otpCode.trim() },
      });
      setEmailStep("OTP_VERIFIED");
      toast({
        title:
          lang === "en"
            ? "OTP verified. You can now enter your new email."
            : "Code OTP vérifié. Vous pouvez saisir votre nouvel e-mail.",
      });
    } catch (c) {
      setEmailError(errorMessage(c));
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmNewEmail = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError(
        lang === "en"
          ? "Enter a valid email address."
          : "Saisissez une adresse e-mail valide.",
      );
      return;
    }
    setEmailBusy(true);
    setEmailError("");
    try {
      await publicApi("/profile/email", {
        method: "PATCH",
        body: { email: trimmed, otp: otpCode.trim() },
      });
      setP((prev) => ({ ...prev, email: trimmed }));
      await d.refresh();
      setEmailStep("IDLE");
      setOtpCode("");
      setNewEmail("");
      toast({
        title:
          lang === "en"
            ? "Email address updated successfully"
            : "Adresse e-mail mise à jour avec succès",
      });
    } catch (c) {
      setEmailError(errorMessage(c));
    } finally {
      setEmailBusy(false);
    }
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const x: Record<string, string> = {};
    if (p.name.trim().length < 2)
      x.name =
        lang === "en"
          ? "Name is required (minimum 2 characters)."
          : "Nom requis (2 caractères minimum).";
    if (p.phone.replace(/\D/g, "").length < 8)
      x.phone = lang === "en" ? "Invalid phone number." : "Numéro invalide.";
    setErr(x);
    if (Object.keys(x).length) return;
    setSaving(true);
    setProfileError("");
    try {
      await d.saveProfile({ ...p, name: p.name.trim(), phone: p.phone.trim() });
      toast({ title: t("settings.profileSavedToast") });
    } catch (c) {
      setProfileError(errorMessage(c));
    } finally {
      setSaving(false);
    }
  };

  const saveChannel = async (ch: Channel) => {
    setSavingCh(ch);
    setPrefError("");
    try {
      await publicApi(`/notification-preferences/${ch}`, {
        method: "PUT",
        body: prefs[ch],
      });
      toast({ title: t("settings.prefSavedToast") });
    } catch (c) {
      setPrefError(errorMessage(c));
    } finally {
      setSavingCh(null);
    }
  };

  return (
    <>
      <PageHead title={t("settings.title")} sub={t("settings.sub")}>
        <EnvironmentBadge />
      </PageHead>

      {/* Settings Navigation Tabs */}
      <div
        className="mb-6 flex flex-wrap items-center gap-2 border-b border-border pb-3"
        role="tablist"
        aria-label={t("settings.title")}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "profile"}
          data-testid="tab-settings-profile"
          onClick={() => setActiveTab("profile")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "profile"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border",
          )}
        >
          <User className="h-4 w-4" />
          <span>{t("settings.tabProfile")}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "security"}
          data-testid="tab-settings-security"
          onClick={() => setActiveTab("security")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "security"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border",
          )}
        >
          <ShieldCheck className="h-4 w-4" />
          <span>{t("settings.tabSecurity")}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "notifications"}
          data-testid="tab-settings-notifications"
          onClick={() => setActiveTab("notifications")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "notifications"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border",
          )}
        >
          <Bell className="h-4 w-4" />
          <span>{t("settings.tabNotifications")}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "fees"}
          data-testid="tab-settings-fees"
          onClick={() => setActiveTab("fees")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "fees"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border",
          )}
        >
          <Percent className="h-4 w-4" />
          <span>{t("settings.tabFees")}</span>
        </button>
      </div>

      {/* TAB 1: PROFILE & LANGUAGE */}
      {activeTab === "profile" && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Panel title={t("settings.profileTitle")}>
            <form
              onSubmit={saveProfile}
              className="grid gap-4"
              noValidate
              data-testid="form-profile"
            >
              {profileError && <ErrorBox message={profileError} />}
              <div className="grid gap-1.5">
                <Label htmlFor="s-name">{t("settings.fullName")}</Label>
                <Input
                  id="s-name"
                  data-testid="input-profile-name"
                  value={p.name}
                  onChange={(e) => setP({ ...p, name: e.target.value })}
                  aria-invalid={!!err.name}
                />
                {err.name && (
                  <p className="text-xs text-destructive">{err.name}</p>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="s-phone">{t("settings.phone")}</Label>
                <Input
                  id="s-phone"
                  data-testid="input-profile-phone"
                  value={p.phone}
                  onChange={(e) => setP({ ...p, phone: e.target.value })}
                  aria-invalid={!!err.phone}
                />
                {err.phone && (
                  <p className="text-xs text-destructive">{err.phone}</p>
                )}
              </div>
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="s-email">{t("settings.email")}</Label>
                  {emailStep === "IDLE" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs"
                      disabled={emailBusy}
                      onClick={() => void requestEmailOtp()}
                      data-testid="button-request-email-otp"
                    >
                      {emailBusy ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {lang === "en"
                        ? "Change email (OTP)"
                        : "Modifier l'e-mail (OTP)"}
                    </Button>
                  )}
                </div>
                <Input
                  id="s-email"
                  value={p.email}
                  readOnly
                  disabled
                  data-testid="text-profile-email"
                />
                {emailError && <ErrorBox message={emailError} />}
                {emailStep === "OTP_SENT" && (
                  <div className="mt-2 space-y-2.5 rounded-xl border border-primary/20 bg-primary/5 p-3">
                    <p className="text-xs text-slate-700">
                      {lang === "en"
                        ? `Enter the 6-digit verification code sent to ${p.email}:`
                        : `Saisissez le code de vérification à 6 chiffres envoyé à ${p.email} :`}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        value={otpCode}
                        onChange={(e) =>
                          setOtpCode(
                            e.target.value.replace(/\D/g, "").slice(0, 6),
                          )
                        }
                        placeholder="123456"
                        maxLength={6}
                        className="w-36 bg-white text-center font-mono text-sm tracking-widest"
                        data-testid="input-email-otp"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={emailBusy || otpCode.length !== 6}
                        onClick={() => void verifyEmailOtp()}
                        data-testid="button-verify-email-otp"
                      >
                        {emailBusy ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <KeyRound className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {lang === "en" ? "Verify OTP" : "Vérifier le code"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={emailBusy}
                        onClick={() => {
                          setEmailStep("IDLE");
                          setOtpCode("");
                          setEmailError("");
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                )}
                {emailStep === "OTP_VERIFIED" && (
                  <div className="mt-2 space-y-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {lang === "en"
                        ? "OTP verified. Enter your new email address:"
                        : "Code OTP vérifié. Saisissez votre nouvelle adresse e-mail :"}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="nouveau@domaine.com"
                        className="min-w-[220px] flex-1 bg-white"
                        data-testid="input-new-email"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={emailBusy || !newEmail.trim()}
                        onClick={() => void confirmNewEmail()}
                        data-testid="button-confirm-new-email"
                      >
                        {emailBusy && (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        )}
                        {lang === "en"
                          ? "Update email"
                          : "Valider le nouvel e-mail"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={emailBusy}
                        onClick={() => {
                          setEmailStep("IDLE");
                          setOtpCode("");
                          setNewEmail("");
                          setEmailError("");
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label>
                  {lang === "en"
                    ? "Account ID (immutable)"
                    : "Matricule (immuable)"}
                </Label>
                <div
                  className="flex h-9 items-center rounded-md border bg-muted px-3 font-mono text-sm"
                  data-testid="text-matricule"
                >
                  {p.matricule || "—"}
                </div>
              </div>
              <Button
                type="submit"
                className="justify-self-start"
                data-testid="button-save-profile"
                disabled={saving}
              >
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {t("settings.saveProfileBtn")}
              </Button>
            </form>
          </Panel>

          <div className="space-y-5">
            <Panel
              title={t("settings.languageSectionTitle")}
              sub={t("settings.languageSectionSub")}
            >
              <div className="flex flex-wrap items-center gap-3 py-1">
                <button
                  type="button"
                  data-testid="settings-lang-fr"
                  onClick={() => setLanguage("fr")}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl border p-3.5 text-xs font-semibold transition-all cursor-pointer",
                    lang === "fr"
                      ? "border-primary bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30"
                      : "border-slate-200 text-muted-foreground hover:bg-accent",
                  )}
                >
                  <LanguageFlag code="fr" className="text-base" />
                  <span>Français (FR)</span>
                  {lang === "fr" && (
                    <span className="ml-2 font-bold text-primary">✓</span>
                  )}
                </button>
                <button
                  type="button"
                  data-testid="settings-lang-en"
                  onClick={() => setLanguage("en")}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl border p-3.5 text-xs font-semibold transition-all cursor-pointer",
                    lang === "en"
                      ? "border-primary bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30"
                      : "border-slate-200 text-muted-foreground hover:bg-accent",
                  )}
                >
                  <LanguageFlag code="en" className="text-base" />
                  <span>English (EN)</span>
                  {lang === "en" && (
                    <span className="ml-2 font-bold text-primary">✓</span>
                  )}
                </button>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* TAB 2: SECURITY & TWO-FACTOR AUTHENTICATION */}
      {activeTab === "security" && (
        <div className="space-y-6">
          <Panel
            title={t("settings.twoFactorTitle")}
            sub={t("settings.twoFactorSub")}
          >
            {twoFactorError && (
              <div className="mb-4">
                <ErrorBox message={twoFactorError} />
              </div>
            )}

            {/* Current 2FA Status Banner */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border bg-card p-4">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-xl",
                    twoFactorData?.enabled
                      ? "bg-emerald-500/10 text-emerald-600"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">
                      {t("settings.twoFactorStatus")}
                    </p>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                        twoFactorData?.enabled
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-700 border border-slate-200",
                      )}
                    >
                      {twoFactorData?.enabled
                        ? t("settings.twoFactorEnabled")
                        : t("settings.twoFactorDisabled")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {twoFactorData?.enabled
                      ? `${t("settings.twoFactorActiveChannel")} : ${twoFactorData.channel}`
                      : t("settings.twoFactorSub")}
                  </p>
                </div>
              </div>

              {twoFactorData?.enabled && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive text-xs"
                  disabled={twoFactorBusy}
                  onClick={() => void disableTwoFactor()}
                >
                  {twoFactorBusy && (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  )}
                  {t("settings.twoFactorDisableBtn")}
                </Button>
              )}
            </div>

            {/* 2FA Configuration / Enablement Flow */}
            {!twoFactorData?.enabled && (
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-bold mb-2 block">
                    {t("settings.twoFactorChannelSelect")}
                  </Label>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {/* EMAIL */}
                    <div
                      onClick={() => setSelected2FaChannel("EMAIL")}
                      className={cn(
                        "relative flex cursor-pointer flex-col justify-between rounded-xl border p-4 transition-all",
                        selected2FaChannel === "EMAIL"
                          ? "border-primary bg-primary/5 shadow-xs"
                          : "border-border hover:border-muted-foreground/30",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <Mail className="h-5 w-5 text-primary" />
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
                          {lang === "fr" ? "Disponible" : "Available"}
                        </span>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs font-bold text-foreground">
                          {t("settings.twoFactorChannelEmail")}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                          {p.email}
                        </p>
                      </div>
                    </div>

                    {/* SMS */}
                    {(() => {
                      const smsAvailable = Boolean(
                        channelAvailability.SMS?.available,
                      );
                      return (
                        <div
                          onClick={() => {
                            if (smsAvailable) setSelected2FaChannel("SMS");
                          }}
                          className={cn(
                            "relative flex flex-col justify-between rounded-xl border p-4 transition-all",
                            smsAvailable
                              ? "cursor-pointer hover:border-muted-foreground/30"
                              : "cursor-not-allowed opacity-60 bg-muted/30",
                            selected2FaChannel === "SMS" && smsAvailable
                              ? "border-primary bg-primary/5 shadow-xs"
                              : "border-border",
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-xs">SMS</span>
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border",
                                smsAvailable
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200",
                              )}
                            >
                              {smsAvailable
                                ? lang === "fr"
                                  ? "Disponible"
                                  : "Available"
                                : t("settings.channelComingSoon")}
                            </span>
                          </div>
                          <div className="mt-3">
                            <p className="text-xs font-bold text-foreground">
                              {t("settings.twoFactorChannelSms")}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                              {p.phone ||
                                (lang === "fr"
                                  ? "Non configuré"
                                  : "Not configured")}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* WHATSAPP */}
                    {(() => {
                      const waAvailable = Boolean(
                        channelAvailability.WHATSAPP?.available,
                      );
                      return (
                        <div
                          onClick={() => {
                            if (waAvailable) setSelected2FaChannel("WHATSAPP");
                          }}
                          className={cn(
                            "relative flex flex-col justify-between rounded-xl border p-4 transition-all",
                            waAvailable
                              ? "cursor-pointer hover:border-muted-foreground/30"
                              : "cursor-not-allowed opacity-60 bg-muted/30",
                            selected2FaChannel === "WHATSAPP" && waAvailable
                              ? "border-primary bg-primary/5 shadow-xs"
                              : "border-border",
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-xs text-[#25D366]">
                              WhatsApp
                            </span>
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border",
                                waAvailable
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200",
                              )}
                            >
                              {waAvailable
                                ? lang === "fr"
                                  ? "Disponible"
                                  : "Available"
                                : t("settings.channelComingSoon")}
                            </span>
                          </div>
                          <div className="mt-3">
                            <p className="text-xs font-bold text-foreground">
                              {t("settings.twoFactorChannelWhatsApp")}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                              {p.phone ||
                                (lang === "fr"
                                  ? "Non configuré"
                                  : "Not configured")}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {twoFactorStep === "IDLE" && (
                  <Button
                    type="button"
                    disabled={twoFactorBusy}
                    onClick={() => void sendTwoFactorOtp()}
                    className="gap-2"
                  >
                    {twoFactorBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4" />
                    )}
                    {twoFactorBusy
                      ? t("settings.twoFactorSending")
                      : t("settings.twoFactorSendOtpBtn")}
                  </Button>
                )}

                {twoFactorStep === "OTP_SENT" && (
                  <div className="space-y-4 rounded-xl border border-primary/20 bg-primary/5 p-4 max-w-md">
                    <div>
                      <Label
                        htmlFor="2fa-otp"
                        className="text-xs font-semibold"
                      >
                        {t("settings.twoFactorOtpLabel")}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Code envoyé à {twoFactorDestination || p.email}
                      </p>
                    </div>

                    <Input
                      id="2fa-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFactorOtp}
                      onChange={(e) => setTwoFactorOtp(e.target.value)}
                      placeholder={t("settings.twoFactorOtpPlaceholder")}
                      className="font-mono text-center tracking-widest text-lg h-11"
                    />

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        disabled={
                          twoFactorBusy || twoFactorOtp.trim().length !== 6
                        }
                        onClick={() => void confirmTwoFactorOtp()}
                        className="gap-2"
                      >
                        {twoFactorBusy && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        {t("settings.twoFactorConfirmBtn")}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={twoFactorBusy}
                        onClick={() => void sendTwoFactorOtp()}
                      >
                        {lang === "fr" ? "Renvoyer" : "Resend"}
                      </Button>
                    </div>
                  </div>
                )}

                {twoFactorStep === "RECOVERY_DISPLAY" && (
                  <div className="space-y-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="flex items-center gap-2 text-emerald-800">
                      <CheckCircle2 className="h-5 w-5" />
                      <p className="text-sm font-bold">
                        {t("settings.twoFactorRecoveryTitle")}
                      </p>
                    </div>
                    <p className="text-xs text-slate-600">
                      {t("settings.twoFactorRecoverySub")}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 font-mono text-xs">
                      {recoveryCodes.map((code) => (
                        <div
                          key={code}
                          className="rounded-lg bg-card border px-3 py-1.5 text-center font-bold"
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setTwoFactorStep("IDLE")}
                    >
                      {t("settings.twoFactorRecoveryClose")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* TAB 3: NOTIFICATION PREFERENCES */}
      {activeTab === "notifications" && (
        <Panel
          title={t("settings.notificationsTitle")}
          sub={t("settings.notificationsSub")}
        >
          {prefError && (
            <div className="mb-3">
              <ErrorBox message={prefError} />
            </div>
          )}
          <ul className="divide-y" data-testid="notification-preferences-list">
            {channelsMeta.map(([ch, label, sub]) => {
              const available = isChannelAvailable(ch);
              return (
                <li key={ch} className="space-y-3 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <Label
                      htmlFor={`pref-${ch}`}
                      className={cn(
                        available
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-75",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="block text-sm font-semibold">
                          {label}
                        </span>
                        {!available && (
                          <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-600 border border-amber-500/20">
                            {t("settings.channelComingSoon")}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-normal text-muted-foreground">
                        {sub}
                      </span>
                      {!available && (
                        <span className="block text-[11px] text-amber-600/90 font-medium mt-0.5">
                          {t("settings.channelComingSoonTooltip")}
                        </span>
                      )}
                    </Label>
                    <Switch
                      id={`pref-${ch}`}
                      data-testid={`switch-pref-${ch.toLowerCase()}`}
                      checked={available && prefs[ch].enabled}
                      disabled={!available}
                      onCheckedChange={(v) =>
                        setPrefs({
                          ...prefs,
                          [ch]: { ...prefs[ch], enabled: v },
                        })
                      }
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {eventsMeta.map(([ev, evLabel]) => {
                      const on = prefs[ch].events.includes(ev);
                      return (
                        <button
                          key={ev}
                          type="button"
                          disabled={!available || !prefs[ch].enabled}
                          data-testid={`toggle-event-${ch.toLowerCase()}-${ev}`}
                          onClick={() =>
                            setPrefs({
                              ...prefs,
                              [ch]: {
                                ...prefs[ch],
                                events: on
                                  ? prefs[ch].events.filter((x) => x !== ev)
                                  : [...prefs[ch].events, ev],
                              },
                            })
                          }
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition disabled:opacity-40 ${on && available ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
                        >
                          {evLabel}{" "}
                          <span className="font-mono opacity-60">{ev}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`button-save-pref-${ch.toLowerCase()}`}
                    disabled={!available || savingCh !== null}
                    onClick={() => void saveChannel(ch)}
                  >
                    {savingCh === ch && (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    )}
                    {t("common.save")} {label}
                  </Button>
                </li>
              );
            })}
          </ul>
        </Panel>
      )}

      {/* TAB 3: FEES & PRICING (COLLECTION & DISTRIBUTION) */}
      {activeTab === "fees" && (
        <div className="space-y-5" data-testid="settings-fees-panel">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border bg-card p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  COLLECTION / PAYIN
                </span>
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                  {collectionPct}%
                </span>
              </div>
              <p className="mt-2 text-2xl font-extrabold">{collectionPct} %</p>
              <p className="mt-1 text-xs font-semibold text-foreground">
                {t("settings.collectionFeeTitle")}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {t("settings.collectionFeeDesc")}
              </p>
            </div>

            <div className="rounded-xl border bg-card p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                  DISTRIBUTION / PAYOUT
                </span>
                <span className="rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                  {distributionPct}%
                </span>
              </div>
              <p className="mt-2 text-2xl font-extrabold">
                {distributionPct} %
              </p>
              <p className="mt-1 text-xs font-semibold text-foreground">
                {t("settings.distributionFeeTitle")}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {t("settings.distributionFeeDesc")}
              </p>
            </div>

            <div className="rounded-xl border bg-card p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  BCP → BCP
                </span>
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                  {internalTransferPct}%
                </span>
              </div>
              <p className="mt-2 text-2xl font-extrabold">
                {internalTransferPct} %
              </p>
              <p className="mt-1 text-xs font-semibold text-foreground">
                {t("settings.internalTransferFeeTitle")}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {t("settings.internalTransferFeeDesc")}
              </p>
            </div>
          </div>

          <Panel
            title={t("settings.feeSimulatorTitle")}
            sub={t("settings.feeSimulatorSub")}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="grid gap-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="sim-op">{t("settings.simOperation")}</Label>
                  <Select
                    value={simOp}
                    onValueChange={(v) =>
                      setSimOp(v as "COLLECTION" | "DISTRIBUTION")
                    }
                  >
                    <SelectTrigger id="sim-op" data-testid="select-sim-op">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="COLLECTION">
                        {t("settings.simOperationCollection")} ({collectionPct}
                        %)
                      </SelectItem>
                      <SelectItem value="DISTRIBUTION">
                        {t("settings.simOperationDistribution")} (
                        {distributionPct}%)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="sim-amount">
                      {t("settings.simAmount")}
                    </Label>
                    <Input
                      id="sim-amount"
                      inputMode="decimal"
                      value={simAmount}
                      onChange={(e) => setSimAmount(e.target.value)}
                      data-testid="input-sim-amount"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="sim-currency">
                      {t("settings.simCurrency")}
                    </Label>
                    <Select value={simCurrency} onValueChange={setSimCurrency}>
                      <SelectTrigger id="sim-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["XAF", "XOF", "GNF", "CDF", "USD", "EUR"].map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {simOp === "COLLECTION" && (
                  <div className="grid gap-1.5">
                    <Label htmlFor="sim-payer">
                      {t("settings.simFeePayer")}
                    </Label>
                    <Select
                      value={simPayer}
                      onValueChange={(v) =>
                        setSimPayer(v as "MERCHANT" | "CUSTOMER")
                      }
                    >
                      <SelectTrigger
                        id="sim-payer"
                        data-testid="select-sim-payer"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERCHANT">
                          {t("settings.simPayerMerchant")}
                        </SelectItem>
                        <SelectItem value="CUSTOMER">
                          {t("settings.simPayerCustomer")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-center rounded-xl border bg-accent/40 p-5">
                <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                  <Calculator className="h-4 w-4" />
                  <span>
                    {simOp === "COLLECTION"
                      ? t("settings.simOperationCollection")
                      : t("settings.simOperationDistribution")}
                  </span>
                </div>

                {simOp === "COLLECTION" ? (
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">
                        {t("settings.simCustomerPays")}
                      </dt>
                      <dd
                        className="font-mono font-bold"
                        data-testid="sim-charged-amount"
                      >
                        {fmtAmount(simResult.charged ?? 0, simCurrency)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">
                        {t("settings.simFeeAmount")} ({simResult.rate}%)
                      </dt>
                      <dd
                        className="font-mono font-semibold text-amber-700"
                        data-testid="sim-fee-amount"
                      >
                        {fmtAmount(simResult.fee, simCurrency)}
                      </dd>
                    </div>
                    <div className="border-t pt-3 flex items-center justify-between">
                      <dt className="font-semibold text-foreground">
                        {t("settings.simMerchantNet")}
                      </dt>
                      <dd
                        className="font-mono text-base font-extrabold text-emerald-700"
                        data-testid="sim-net-amount"
                      >
                        {fmtAmount(simResult.net ?? 0, simCurrency)}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">
                        {t("settings.simBeneficiaryReceives")}
                      </dt>
                      <dd
                        className="font-mono font-bold text-emerald-700"
                        data-testid="sim-payout-amount"
                      >
                        {fmtAmount(simResult.payout ?? 0, simCurrency)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">
                        {t("settings.simFeeAmount")} ({simResult.rate}% · Mode
                        B)
                      </dt>
                      <dd
                        className="font-mono font-semibold text-amber-700"
                        data-testid="sim-fee-amount"
                      >
                        + {fmtAmount(simResult.fee, simCurrency)}
                      </dd>
                    </div>
                    <div className="border-t pt-3 flex items-center justify-between">
                      <dt className="font-semibold text-foreground">
                        {t("settings.simWalletTotalDebit")}
                      </dt>
                      <dd
                        className="font-mono text-base font-extrabold text-foreground"
                        data-testid="sim-total-debit"
                      >
                        {fmtAmount(simResult.totalDebit ?? 0, simCurrency)}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}
